import { useEffect, useRef } from 'preact/hooks';
import type { RefObject } from 'preact';

/**
 * **Le comportement commun aux quatre surcouches** — visionneuse, lecteur,
 * éditeur, sélecteur de collection.
 *
 * Les quatre déclaraient `role="dialog" aria-modal="true"`. Deux seulement
 * fermaient sur `Échap`, aucune ne contenait le focus, et aucune ne le rendait en
 * se fermant. `aria-modal` est pourtant une promesse : il dit aux technologies
 * d'assistance que **le reste de la page n'existe plus**. Une surcouche qui
 * l'annonce sans retenir la tabulation envoie le curseur dans la page qu'elle
 * couvre — au clavier, on se retrouve à parcourir une liste qu'on ne voit pas.
 *
 * Trois choses, donc, et elles vont ensemble :
 *
 * ① **`Échap` ferme** — la première sortie que tout le monde essaie ;
 * ② **la tabulation boucle** à l'intérieur, dans les deux sens ;
 * ③ **le focus revient d'où il venait** à la fermeture, sinon il repart au début
 *    du document et l'on a perdu sa place dans la grille.
 *
 * Le verrouillage du défilement est optionnel parce qu'il ne se justifie pas
 * partout : une visionneuse ouverte au milieu d'une photothèque doit empêcher la
 * page de bouger dessous — on la refermerait ailleurs qu'où on l'a ouverte.
 */
export function useDialog(
  active: boolean,
  options: {
    /** Omis, `Échap` ne fait rien — l'éditeur garde sa propre règle (un conflit retient). */
    readonly onEscape?: () => void;
    readonly lockScroll?: boolean;
  } = {},
): RefObject<HTMLDivElement> {
  const host = useRef<HTMLDivElement>(null);
  // La fonction est relue à chaque événement plutôt que capturée : sans cela, une
  // fermeture qui change d'identité à chaque rendu ferait remonter tout l'effet.
  const escape = useRef(options.onEscape);
  escape.current = options.onEscape;

  useEffect(() => {
    if (!active) return;
    const node = host.current;
    const previous = document.activeElement as HTMLElement | null;

    /** Ce qui peut recevoir le focus, dans l'ordre du document. */
    const focusables = (): HTMLElement[] =>
      [
        ...(node?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? []),
      ].filter((el) => el.offsetParent !== null || el === document.activeElement);

    // Le premier élément, ou le conteneur lui-même : une surcouche qui n'a rien de
    // focalisable — un lecteur en cours de chargement — doit tout de même prendre
    // le focus, faute de quoi `Échap` irait à la page du dessous.
    const first = focusables()[0];
    if (first) first.focus();
    else node?.focus();

    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') {
        const close = escape.current;
        if (close) {
          ev.stopPropagation();
          close();
        }
        return;
      }
      if (ev.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) {
        ev.preventDefault();
        return;
      }
      const edge = ev.shiftKey ? list[0]! : list[list.length - 1]!;
      if (document.activeElement === edge || !node?.contains(document.activeElement)) {
        ev.preventDefault();
        (ev.shiftKey ? list[list.length - 1]! : list[0]!).focus();
      }
    };

    document.addEventListener('keydown', onKey, true);

    const previousOverflow = document.body.style.overflow;
    if (options.lockScroll) document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (options.lockScroll) document.body.style.overflow = previousOverflow;
      // **Le focus revient**, et seulement s'il est encore quelque part : rendre
      // le focus à un bouton qui a été démonté avec la surcouche lèverait.
      if (previous?.isConnected) previous.focus();
    };
  }, [active, options.lockScroll]);

  return host;
}
